/**
 * Opening storage and getting the character this tab plays as.
 *
 * Two decisions, both provisional, both here so the rest of the app takes a profile and asks
 * nothing else. Which store: IndexedDB, or memory when there is none, because a private window with
 * storage switched off should still be able to play a round. Which profile: the most recently
 * saved one, or a new one under a rolled name when there is none.
 *
 * The most-recent rule is a placeholder for a character picker, which belongs to the hub screen
 * (#28) along with creating and deleting a character on purpose. What is not provisional is that
 * only one place decides, so the picker replaces a function rather than a habit.
 *
 * A fallback store is reported rather than hidden. Nothing survives a reload in memory, and a
 * player who is about to lose an hour is owed the sentence before they spend it.
 */

import { isIndexedDbAvailable, openIndexedDbBackend } from '@/save/indexedDbBackend';
import { createMemorySaveBackend } from '@/save/memorySaveBackend';
import { openProfileStore } from '@/save/profileStore';
import { detailOf } from '@/save/saveBackendContract';
import { rollPlayerName } from '@/identity/rollPlayerName';
import type { ProfileStore, ProfileStoreOptions } from '@/save/profileStore';
import type { SaveProfile } from '@/save/saveContract';
import type { SaveFailure, SaveResult } from '@/save/saveResult';

export interface SaveStorage {
  readonly store: ProfileStore;
  /** False when this is the in-memory fallback, so nothing here outlives the tab. */
  readonly durable: boolean;
  /** Why it is not durable, when it is not. */
  readonly problem: SaveFailure | null;
}

export async function openSaveStorage(options: ProfileStoreOptions = {}): Promise<SaveStorage> {
  if (!isIndexedDbAvailable()) {
    return {
      store: await openProfileStore(createMemorySaveBackend(), options),
      durable: false,
      problem: { reason: 'storageUnavailable', detail: 'this browser exposes no IndexedDB' },
    };
  }

  try {
    return {
      store: await openProfileStore(await openIndexedDbBackend(), options),
      durable: true,
      problem: null,
    };
  } catch (error) {
    return {
      store: await openProfileStore(createMemorySaveBackend(), options),
      durable: false,
      problem: { reason: 'storageUnavailable', detail: detailOf(error) },
    };
  }
}

/**
 * The profile to play as: the newest save, or a fresh character.
 *
 * A record the build cannot read is skipped by `list` rather than deleted, so a save from a newer
 * build does not become "no profiles" and get quietly replaced by an empty one. It becomes a
 * profile that is not the newest readable one, and the failure is still there to be reported by
 * whoever eventually asks for it by id.
 */
export async function resolveActiveProfile(
  store: ProfileStore,
  rollName: () => string = rollPlayerName,
): Promise<SaveResult<SaveProfile>> {
  const summaries = await store.list();
  if (!summaries.ok) return summaries;

  const newest = summaries.value[0];
  if (newest === undefined) return store.create(rollName());

  return store.load(newest.profileId);
}
